/* ============================================================================
   THE SCRATCH CARD

   Four stages on one panel: scratch → won → email → done. The section owns the
   markup, the copy and the geometry — including where the foil sits on the
   owner's artwork — and this file owns the foil itself, the rub, and the one
   network call.

   IT OPENS ITSELF, WHICH IS NEW. Everything before this in the theme waited to
   be asked. The owner lifted that rule on 21 August 2026 ("у нас были запрещены
   поп апы теперь мы делаем их активные"), and the restraint that is left is
   narrow but real: once per visitor until they claim, a snooze measured in days
   after a close, and never again for anyone who has taken the code.

   WHAT CAME ACROSS FROM THE TREAT. The subscribe, and only the subscribe. It
   was the one part of that section verified end to end, including the single
   success signal Shopify's classic customer form gives an async caller — which
   is not a status code and not a JSON body. See the submit handler.
   ========================================================================= */
(() => {
  const KEY = 'cooper:scratch';

  /* The foil's colour ramp, read out of assets/cooper-scratch-art.png rather
     than picked: five columns sampled across the panel at the top of the file's
     own gradient, where the scribble overlay is thinnest. Painting the same ramp
     into the canvas is what lets the panel look exactly like the render until
     the first stroke lands on it. */
  const RAMP = ['#4a9cf0', '#a56fd9', '#e154bc', '#fc5878', '#fe6754'];

  /* --------------------------------------------------------------------------
     MEMORY, AND WHY IT IS WRAPPED.

     localStorage THROWS rather than returning null in Safari's private mode and
     wherever storage is blocked by policy. An unguarded read here would take the
     whole card down for exactly the visitors most likely to be on a phone.
     Failing to remember is a far smaller fault than failing to open, so both
     directions swallow and carry on: the worst case is somebody seeing the card
     again on their next visit.
     ----------------------------------------------------------------------- */
  function remembered() {
    try {
      return JSON.parse(localStorage.getItem(KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function remember(value) {
    try {
      localStorage.setItem(KEY, JSON.stringify(value));
    } catch (e) {
      /* Nothing to do, and nothing worth telling the visitor. */
    }
  }

  const editing = () => Boolean(window.Shopify && window.Shopify.designMode);

  /* --------------------------------------------------------------------------
     THE FOIL.

     PAINTED ON OPEN AND NOT ON LOAD. A closed <dialog> is display: none, so the
     canvas has no box and clientWidth is 0 — sizing the backing store then gives
     a 0×0 canvas and a card that is already fully scratched before anyone has
     touched it. Every paint below happens after showModal().
     ----------------------------------------------------------------------- */
  function paintFoil(dialog) {
    const canvas = dialog.querySelector('[data-scratch-foil]');
    if (!canvas) return;

    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;

    /* Capped at 2. Past that the backing store grows by the square for no
       visible gain on a foil that is about to be rubbed off. */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);

    const grade = ctx.createLinearGradient(0, 0, w, 0);
    for (let i = 0; i < RAMP.length; i += 1) {
      grade.addColorStop(i / (RAMP.length - 1), RAMP[i]);
    }
    ctx.fillStyle = grade;
    ctx.fillRect(0, 0, w, h);

    /* A LITTLE GRAIN, because the render has some and a flat ramp beside a
       photograph of fur reads as a screenshot pasted into a photo. */
    const grains = Math.round((w * h) / 420);
    for (let i = 0; i < grains; i += 1) {
      ctx.fillStyle = i % 2 ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)';
      ctx.fillRect(Math.random() * w, Math.random() * h, 1.6, 1.6);
    }

    /* The dashed border, which is the render's own: white, 13px in from the
       panel edge on a 658px panel, so every figure here is a fraction of the
       width and holds at any size. */
    const inset = Math.max(4, w * 0.0198);
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.lineWidth = Math.max(1, w * 0.0046);
    ctx.setLineDash([w * 0.03, w * 0.021]);
    const radius = Math.max(4, w * 0.026);
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(inset, inset, w - inset * 2, h - inset * 2, radius);
    } else {
      ctx.rect(inset, inset, w - inset * 2, h - inset * 2);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    const label = dialog.dataset.foilLabel || '';
    if (label) {
      /* Kanit if it has arrived, the fallback if it has not. The caller repaints
         once document.fonts settles, so a first frame in the fallback face gets
         corrected before anyone has read it — and never once a rub has started,
         which would put the visitor's own work back. */
      const size = Math.max(11, Math.round(w * 0.082));
      ctx.font = '700 ' + size + 'px Kanit, ' + getComputedStyle(dialog).fontFamily;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(255,255,255,0.96)';
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 4;
      ctx.fillText(label, w / 2, h / 2);
      ctx.shadowBlur = 0;
    }
  }

  /* --------------------------------------------------------------------------
     HOW MUCH IS GONE, WITHOUT READING A SINGLE PIXEL.

     The obvious way to measure a scratch card is getImageData over the backing
     store and count transparent pixels. Two reasons this does not:

     1. IT IS THE EXPENSIVE CALL. Four bytes per pixel over the whole canvas, on
        every few pointermoves, on a phone — that is what turns a scratch card
        into a slideshow.

     2. IT BREAKS THE MOMENT THE FOIL BECOMES A PICTURE. Draw anything from
        cdn.shopify.com into this canvas and it is tainted; getImageData then
        throws, and a progress figure stuck at zero means the card can never be
        opened by rubbing at all. Today the foil is painted from a colour ramp
        and would be readable, but the next person to improve the fidelity by
        drawing the artwork in would discover that by shipping it.

     So progress is a coarse occupancy grid instead: eighteen columns of cells,
     each marked the first time a stroke passes within a brush of its centre.
     Cheap, monotonic, immune to both problems, and it does not over-count
     somebody rubbing the same spot forty times — which a stroke-length estimate
     does, and which would open the card without revealing the prize.
     ----------------------------------------------------------------------- */
  function makeGrid(w, h) {
    const cols = 18;
    const rows = Math.max(6, Math.round((cols * h) / w));
    return {cols, rows, cells: new Uint8Array(cols * rows), marked: 0, w, h};
  }

  function markAt(grid, x, y, radius) {
    const cw = grid.w / grid.cols;
    const ch = grid.h / grid.rows;
    const c0 = Math.max(0, Math.floor((x - radius) / cw));
    const c1 = Math.min(grid.cols - 1, Math.floor((x + radius) / cw));
    const r0 = Math.max(0, Math.floor((y - radius) / ch));
    const r1 = Math.min(grid.rows - 1, Math.floor((y + radius) / ch));
    for (let r = r0; r <= r1; r += 1) {
      for (let c = c0; c <= c1; c += 1) {
        const i = r * grid.cols + c;
        if (grid.cells[i]) continue;
        /* The cell's centre against the brush, so a stroke clipping a corner
           does not claim the whole cell. */
        const dx = (c + 0.5) * cw - x;
        const dy = (r + 0.5) * ch - y;
        if (dx * dx + dy * dy > radius * radius) continue;
        grid.cells[i] = 1;
        grid.marked += 1;
      }
    }
  }

  function wire(dialog) {
    if (dialog.dataset.scratchBound) return;
    dialog.dataset.scratchBound = 'true';

    const canvas = dialog.querySelector('[data-scratch-foil]');
    const title = dialog.querySelector('[data-scratch-title]');
    const faces = [...dialog.querySelectorAll('[data-scratch-face]')];
    const form = dialog.querySelector('[data-scratch-form]');
    const error = dialog.querySelector('[data-scratch-error]');
    const send = dialog.querySelector('[data-scratch-send]');

    const threshold = (Number(dialog.dataset.threshold) || 45) / 100;
    const delay = (Number(dialog.dataset.delay) || 0) * 1000;
    const scrollAt = Number(dialog.dataset.scrollAt) || 0;
    const klaviyoKey = (dialog.dataset.klaviyoKey || '').trim();
    const klaviyoList = (dialog.dataset.klaviyoList || '').trim();
    const remindDays = Number(dialog.dataset.remindDays) || 14;

    let started = false;
    let opened = false;
    let claimed = false;
    let busy = false;
    let steppingAside = false;
    let grid = null;

    /* ---------------------------------------------------------------------
       THE STAGES.

       Focus moves with the stage, and that is not a nicety: <dialog> traps
       focus, so a visitor who reaches the email step with focus still resting
       on a button that no longer exists has focus on nothing and a keyboard
       that does nothing. Moving it also announces the new step, which is the
       only announcement a stage change gets.
       ------------------------------------------------------------------- */
    /* A FACE CAN SERVE MORE THAN ONE STAGE, and the first two share one. Faces
       declare a space-separated list rather than a single stage, which is what
       lets `scratch` and `won` be the same box: the reward is revealed in place
       instead of being swapped out from under the eye. */
    const serves = (face, name) => face.dataset.scratchFace.split(' ').indexOf(name) !== -1;

    function stage(name) {
      dialog.dataset.stage = name;
      for (const face of faces) face.hidden = !serves(face, name);

      if (title) {
        const heading = dialog.dataset['title' + name.charAt(0).toUpperCase() + name.slice(1)];
        if (heading) title.textContent = heading;
      }

      /* NO FOCUS MOVE ON THE FIRST STAGE. The sheet already holds focus from
         its autofocus, and the only control on the card at that point is the
         Claim button — which is invisible until the foil is gone. */
      if (name === 'scratch') return;

      requestAnimationFrame(() => {
        const face = faces.find((f) => serves(f, name));
        if (!face) return;
        /* NOT `input, button`. The email face's first input is the hidden
           contact[tags] field, a hidden input cannot take focus, and .focus()
           on it fails silently — measured on the live theme as focus falling
           all the way back to <body> on the step where the visitor is being
           asked to type. */
        const target = face.querySelector('input:not([type="hidden"]), button');
        if (target) target.focus();
      });
    }

    function reveal() {
      if (dialog.dataset.stage !== 'scratch') return;
      if (canvas) canvas.classList.add('cooper-scratch__foil--spent');
      /* Waits out the fade so the two do not read as one jump cut. The number
         matches the transition in the section's stylesheet; if one changes the
         other has to, and the symptom is a card that flips before it clears. */
      setTimeout(() => stage('won'), 400);
    }

    /* ---------------------------------------------------------------------
       THE RUB.

       Pointer events and not mouse+touch pairs: one code path for a finger, a
       mouse, a stylus and a trackpad, and no synthetic-click duplication to
       defend against. setPointerCapture is what keeps a rub alive when the
       finger slides off the panel mid-stroke — without it the stroke ends at
       the edge and the visitor has to start again, which reads as the card
       being broken.
       ------------------------------------------------------------------- */
    if (canvas) {
      const ctx = canvas.getContext('2d');
      let rubbing = false;
      let lastX = 0;
      let lastY = 0;

      const at = (event) => {
        const box = canvas.getBoundingClientRect();
        const scale = box.width ? canvas.width / box.width : 1;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        return {
          x: (event.clientX - box.left) * scale / dpr,
          y: (event.clientY - box.top) * scale / dpr,
        };
      };

      const brush = () => Math.max(16, canvas.clientWidth * 0.11);

      const erase = (from, to) => {
        if (!ctx) return;
        const size = brush();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = size;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        /* A LINE AND NOT A DOT. A finger flicked across the panel fires
           pointermove every few frames, tens of pixels apart; stamping a circle
           at each one leaves a dotted trail with foil between the dots. */
        ctx.lineTo(to.x, to.y);
        ctx.stroke();

        /* The same stroke, walked at half a brush per step so the grid sees a
           continuous line rather than the two endpoints. */
        if (!grid) grid = makeGrid(canvas.clientWidth, canvas.clientHeight);
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const span = Math.sqrt(dx * dx + dy * dy);
        const steps = Math.max(1, Math.ceil(span / (size / 2)));
        for (let i = 0; i <= steps; i += 1) {
          markAt(grid, from.x + (dx * i) / steps, from.y + (dy * i) / steps, size / 2);
        }
      };

      const enough = () => grid && grid.marked / grid.cells.length >= threshold;

      canvas.addEventListener('pointerdown', (event) => {
        rubbing = true;
        started = true;
        const point = at(event);
        lastX = point.x;
        lastY = point.y;
        erase(point, point);
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch (e) {
          /* Capture is a convenience; losing it costs one stroke, not the card. */
        }
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!rubbing) return;
        const point = at(event);
        erase({x: lastX, y: lastY}, point);
        lastX = point.x;
        lastY = point.y;
        if (enough()) {
          rubbing = false;
          reveal();
        }
      });

      const stop = () => {
        rubbing = false;
        if (enough()) reveal();
      };
      canvas.addEventListener('pointerup', stop);
      canvas.addEventListener('pointercancel', stop);
      canvas.addEventListener('pointerleave', () => { rubbing = false; });
    }

    const skip = dialog.querySelector('[data-scratch-reveal]');
    if (skip) skip.addEventListener('click', reveal);

    const claim = dialog.querySelector('[data-scratch-claim]');
    if (claim) claim.addEventListener('click', () => stage('email'));

    /* ---------------------------------------------------------------------
       THE SUBSCRIBE.

       ASYNC AND NOT A NAVIGATION. A page load would close the dialog, throw
       away the stage the visitor is standing on and land them back at the top
       of the page holding nothing — the code is on face four, which only exists
       in this document.
       ------------------------------------------------------------------- */
    /* ---------------------------------------------------------------------
       THE SECOND DESTINATION.

       On the owner's call: "я буду отправлять через klavio потом / по этому
       эмейл должен уходить и в shopify и klavio". Shopify keeps the customer;
       Klaviyo is where the flow that mails the code will live, and a flow
       triggers on list membership — so the address has to arrive in a list, not
       merely exist as a profile.

       THE CLIENT SUBSCRIPTION ENDPOINT AND NOT THE ADMIN ONE. Klaviyo publishes
       /client/subscriptions specifically for calls from a page, authenticated by
       the PUBLIC key: it is CORS-enabled and it cannot read anything back. The
       private key would work too and must never be in a theme — a theme file is
       served to everybody, so a private key in one is a private key published.

       THE REVISION IS PINNED because Klaviyo's API is dated, not versioned by
       path: an unpinned call gets whatever is current and breaks on a schedule
       nobody here controls. 2026-07-15 is the revision this was written against;
       if Klaviyo ever retires it, this one string is the fix.
       ------------------------------------------------------------------- */
    async function toKlaviyo(email) {
      if (!klaviyoKey || !klaviyoList) return 'skipped';
      try {
        const answer = await fetch(
          'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(klaviyoKey),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: '2026-07-15',
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  /* Shows up on the profile in Klaviyo, so a year from now it is
                     obvious which of the shop's forms brought somebody in. */
                  custom_source: 'Scratch card',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email: email,
                        subscriptions: {
                          email: {marketing: {consent: 'SUBSCRIBED'}},
                        },
                      },
                    },
                  },
                },
                relationships: {
                  list: {data: {type: 'list', id: klaviyoList}},
                },
              },
            }),
          }
        );
        /* 202 Accepted, with no body. */
        return answer.ok ? 'ok' : 'failed:' + answer.status;
      } catch (e) {
        return 'failed:network';
      }
    }

    const wait = (ms) => new Promise((res) => setTimeout(res, ms));

    /* ---------------------------------------------------------------------
       THE CAPTCHA, WHICH IS NOT OPTIONAL AND WAS NOT OBVIOUS.

       Shopify protects customer forms with invisible hCaptcha, and it does the
       wiring itself: by the time this runs it has already inserted a
       `.h-captcha` container and an `h-captcha-response` field INTO THIS FORM
       and rendered a widget whose id it leaves on the container. On a native
       submit its own handler solves the challenge and fills the field.

       This form is posted with fetch, so that handler never runs, the field goes
       up empty, and /contact answers 400 with a page titled "Missing CAPTCHA
       token" — measured on the live theme. The visitor sees the error line and
       never reaches the code. So the token is fetched here instead.

       AND THEN THE PART THAT ACTUALLY MATTERS. hCaptcha does not always pass
       silently: when it wants proof it puts a 320x400 iframe on the page, and
       that iframe is a sibling of the dialog rather than a child of it. A
       <dialog> opened with showModal() is in the top layer, which paints over
       every z-index there is — so the challenge is visible and untouchable, and
       then it expires. Measured exactly that way: 120 seconds, then
       "challenge-expired".

       Moving the iframe into the dialog is not a fix. Re-inserting an <iframe>
       anywhere else in the document reloads it, which throws away the challenge
       it was in the middle of.

       So the card steps aside. A passive pass lands in a few hundred
       milliseconds and nobody sees anything; if it has not landed by then there
       is a challenge on the page, the dialog closes so it can be reached, and it
       reopens on the same stage when the token settles. The stage lives in an
       attribute rather than in the dialog's open state, so nothing is lost.
       ------------------------------------------------------------------- */
    async function solveCaptcha() {
      const holder = form.querySelector('[data-hcaptcha-widget-id]');
      if (!holder || !window.hcaptcha) {
        /* Captcha off for this shop, or Shopify changed how it renders it. Post
           without a token — which is exactly what worked before it was on. */
        return {token: '', required: false};
      }

      const id = holder.dataset.hcaptchaWidgetId;

      let already = '';
      try {
        already = window.hcaptcha.getResponse(id) || '';
      } catch (e) {
        already = '';
      }
      if (already) return {token: already, required: true, id: id};

      let settled = false;
      const run = window.hcaptcha
        .execute(id, {async: true})
        .then((r) => (r && r.response) || '')
        .catch(() => '')
        .then((t) => {
          settled = true;
          return t;
        });

      await Promise.race([run, wait(1200)]);
      if (settled) return {token: await run, required: true, id: id};

      /* THE CHALLENGE IS UP. Off to one side, and back afterwards whatever
         happens — the reopen is in a finally, because every way out of here
         leads to a visitor looking at a page with no card on it. */
      steppingAside = true;
      dialog.close();
      let token = '';
      try {
        /* A CEILING, AND IT IS NOT DEFENSIVE PROGRAMMING FOR ITS OWN SAKE. Two
           things were measured on the live theme: a challenge nobody touches
           sits for a full 120 seconds before hCaptcha reports it expired, and
           hcaptcha.close() — the visitor shutting the challenge — does not
           settle the promise at all, ever. Either one leaves the card off the
           screen indefinitely with nothing to explain where it went.

           Forty-five seconds is long enough for anybody genuinely working
           through a puzzle and short enough that giving up is not abandonment.
           Past it the address has usually already reached Klaviyo, which needs
           no captcha, so the code still arrives. */
        token = await Promise.race([run, wait(45000).then(() => '')]);
      } finally {
        steppingAside = false;
        if (!dialog.open) dialog.showModal();
      }
      return {token: token || '', required: true, id: id};
    }

    if (form) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (busy) return;
        busy = true;
        if (error) error.hidden = true;
        if (send) send.setAttribute('aria-busy', 'true');

        const address = (dialog.querySelector('[data-scratch-email]') || {}).value || '';

        /* BOTH AT ONCE. They are independent destinations and Klaviyo needs no
           captcha, so running them in sequence would leave somebody looking at
           a spinner for the length of both for no gain. Klaviyo goes first and
           unconditionally — it is the one that will send the mail. */
        const klaviyoRun = toKlaviyo(address);

        const shopifyRun = (async () => {
          const captcha = await solveCaptcha();
          if (captcha.required && !captcha.token) return 'no-captcha-token';
          const body = new FormData(form);
          if (captcha.token) body.set('h-captcha-response', captcha.token);
          try {
            const answer = await fetch(form.action, {
              method: 'POST',
              body: body,
              headers: {Accept: 'text/html'},
            });
            /* THE ONLY SUCCESS SIGNAL THIS FORM GIVES AN ASYNC CALLER.
               /contact answers with a redirect back to the page carrying
               ?customer_posted=true, so the marker is in the FINAL url after
               fetch has followed it. There is no JSON body and there are no
               typed error codes: response.ok alone is true for a page that
               rendered a validation error — or the captcha refusal — just as
               happily as for a subscribe. */
            const good = answer.ok && answer.url.indexOf('customer_posted=true') !== -1;
            /* A TOKEN IS GOOD ONCE. Leaving a spent one in the widget means a
               retry posts a stale token and is refused, which reads as the
               address being rejected rather than the attempt. */
            if (captcha.id && window.hcaptcha) {
              try {
                window.hcaptcha.reset(captcha.id);
              } catch (e) {
                /* Nothing to do; the next execute() makes its own. */
              }
            }
            return good ? 'ok' : 'refused:' + answer.status;
          } catch (e) {
            return 'failed:network';
          }
        })();

        const [shopify, klaviyo] = await Promise.all([shopifyRun, klaviyoRun]);

        /* EITHER DESTINATION IS ENOUGH TO HAND OVER THE CODE, and that is a
           deliberate change from gating on Shopify alone. The visitor did their
           part; withholding a real discount because a captcha timed out or a
           third party answered 500 punishes them for our plumbing, and the code
           on the next face is valid either way.

           If NEITHER took it, nothing was captured and saying so is the honest
           answer — that is the error line.

           Both failures are named in the console, because a silent one here
           means an empty list and a flow that never fires, and the owner needs
           some way to learn that other than waiting for no emails. */
        const ok = shopify === 'ok' || klaviyo === 'ok';

        if (shopify !== 'ok') {
          console.warn('[cooper-scratch] Shopify did not take the address (' + shopify + ').');
        }
        if (klaviyo !== 'ok' && klaviyo !== 'skipped') {
          console.warn(
            '[cooper-scratch] Klaviyo did not take the address (' +
              klaviyo +
              '). Check the public API key and the list ID in the Scratch card section.'
          );
        }

        busy = false;
        if (send) send.removeAttribute('aria-busy');

        if (!ok) {
          if (error) error.hidden = false;
          return;
        }

        /* CLAIMED IS WRITTEN HERE AND NOT ON THE CLAIM BUTTON. Anyone who
           closed the card at the email step has not had the code, and asking
           them again in a fortnight is the right behaviour rather than a second
           nag at somebody who already said yes. */
        claimed = true;
        remember({c: 1});
        stage('done');
        applyDiscount();
      });
    }

    const copy = dialog.querySelector('[data-scratch-copy]');
    if (copy) {
      copy.addEventListener('click', async () => {
        const code = dialog.querySelector('[data-scratch-code]');
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code.textContent.trim());
          copy.textContent = copy.dataset.copiedLabel || 'Copied';
        } catch (e) {
          /* THE CLIPBOARD IS REFUSED SILENTLY on any page the browser does not
             consider secure, and inside several in-app browsers regardless. The
             code carries user-select: all, so selecting it is the fallback and
             this makes the selection for them. */
          const range = document.createRange();
          range.selectNodeContents(code);
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      });
    }

    /* ---------------------------------------------------------------------
       CLOSING.

       Anything that is not a claim is a snooze. The `close` event covers all
       three ways out — the button, Escape and a click on the scrim — which is
       why the close button calls close() rather than doing the bookkeeping
       itself.
       ------------------------------------------------------------------- */
    /* querySelectorAll AND NOT querySelector. There are two of these now: the X
       in the corner, and the button on the last face when the card is running
       without a discount — that face has no code to copy, so its one control is
       a way out. A single querySelector would have wired the X and left the
       other one dead, which looks exactly like a broken card on the final step. */
    for (const closer of dialog.querySelectorAll('[data-scratch-close]')) {
      closer.addEventListener('click', () => dialog.close());
    }

    dialog.addEventListener('click', (event) => {
      /* A click on the scrim lands on the dialog itself, because ::backdrop is
         not an element and cannot be a target. The sheet fills the dialog, so
         anything reaching this element came from outside the sheet. */
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', () => {
      if (editing()) return;
      /* NOT A DISMISSAL. The card closes itself for the length of a captcha
         challenge — see solveCaptcha — and treating that as "they closed it"
         would snooze the popup for a fortnight in the middle of somebody
         subscribing. */
      if (steppingAside) return;
      if (claimed) return;
      /* Object.assign because remember() replaces the stored object outright,
         and the strip keeps its own dismissal in there. */
      remember(Object.assign(remembered(), { u: Date.now() + remindDays * 86400000 }));
      showTab();
    });

    /* ---------------------------------------------------------------------
       OPENING. The paint happens after showModal, and the reason is at
       paintFoil.
       ------------------------------------------------------------------- */
    /* ---------------------------------------------------------------------
       THE STRIP AT THE EDGE.

       It exists so that closing the card is not the end of the offer. Shown
       only to somebody who has already met the card and let it go, never to
       somebody who took the code, and never in the editor, where an element
       pinned to the viewport is only in the way.

       reopen() rather than open(): open() latches on `opened` so the card
       cannot arrive twice by itself, and that latch is exactly right for the
       triggers and exactly wrong for a button somebody pressed.

       The room-is-busy guards are in CSS, not here: they have to react to a
       drawer or a filter opening at any moment, and :has() does that without
       this file subscribing to every panel in the theme.
       ------------------------------------------------------------------- */
    const tab = document.querySelector('[data-scratch-tab]');

    const showTab = () => {
      if (!tab || editing()) return;
      const m = remembered();
      if (m.c) return;
      if (m.t && Date.now() < m.t) return;
      tab.hidden = false;
    };

    const hideTab = () => { if (tab) tab.hidden = true; };

    const reopen = () => {
      if (dialog.open) return;
      hideTab();
      opened = true;
      paintArt();
      dialog.showModal();
      grid = null;
      paintFoil(dialog);
    };

    if (tab) {
      tab.querySelector('[data-scratch-tab-open]').addEventListener('click', reopen);
      tab.querySelector('[data-scratch-tab-dismiss]').addEventListener('click', () => {
        hideTab();
        /* Merged, not replaced: remember() writes the whole object, and the
           snooze the card set on its way out lives in the same one. */
        remember(Object.assign(remembered(), { t: Date.now() + remindDays * 86400000 }));
      });
    }

    /* Somebody who met the card on an earlier page and closed it arrives here
       with the snooze already written, and the strip is how the offer stays
       reachable for them. */
    if (!editing()) {
      const seen = remembered();
      if (seen.u && !seen.c && !(seen.t && Date.now() < seen.t)) showTab();
    }

    /* DECLARED HERE, ABOVE THE SNOOZE, AND IT HAS TO STAY ABOVE IT.

       Further down, a visitor carrying a snooze returns out of this function
       early. A `const` below that line is not merely undefined for them, it is
       unreachable for the life of the page — and the strip at the edge is shown
       to nobody BUT people carrying a snooze. Declared after it, this threw
       "Cannot access before initialization" on every press of the one control
       that could reach it, while every test that opened the card normally
       passed. Moving it changes nothing else: it closes over `dialog` alone. */
    /* THE ARTWORK IS FETCHED WHEN THE TRIGGER ARMS, not when the page loads.
       sections/cooper-scratch.liquid ships the url in data-scratch-art-src
       and no src at all — see the reasoning there. Painting it at arming
       time rather than at opening time keeps the card from appearing empty:
       the trigger arms 1.5s in and fires no earlier than 35% scroll depth,
       so the file has the whole reading time to arrive. */
    const paintArt = () => {
      for (const art of dialog.querySelectorAll('[data-scratch-art-src]')) {
        const src = art.dataset.scratchArtSrc || '';
        if (src && art.getAttribute('src') !== src) art.setAttribute('src', src);
      }
    };

    /* ---------------------------------------------------------------------
       THE CODE PUTS ITSELF ON THE BASKET.

       /discount/<code> is Shopify's own way in, and `redirect: "manual"`
       takes the cookie without following the 302 into a whole home page.
       Measured live: 2990 -> 2691, total_discount 299, and COOPER10 in
       cart_level_discount_applications, identical to visiting the URL.

       THE READ-BACK IS THE POINT. On 25 August that same redirect answered
       302 for a code that did not exist, with /cart.js reading total_discount
       0 either side of it — a 302 proves nothing. So the wording under the
       card only changes if the basket is seen to carry the code. Nothing is
       claimed otherwise, and the code and its Copy button are on screen
       either way, which is also what somebody on a second device needs.
       ------------------------------------------------------------------- */
    const applyDiscount = () => {
      const node = dialog.querySelector('[data-scratch-code]');
      const code = node ? node.textContent.trim() : '';
      if (!code) return;
      fetch('/discount/' + encodeURIComponent(code), { redirect: 'manual', credentials: 'same-origin' })
        .then(() => fetch('/cart.js', { credentials: 'same-origin' }))
        .then((r) => r.json())
        .then((cart) => {
          const on = (cart.cart_level_discount_applications || [])
            .some((d) => String(d.title).toLowerCase() === code.toLowerCase());
          if (!on) return;
          const note = dialog.querySelector('[data-scratch-note="done"]');
          if (note && note.dataset.appliedNote) note.textContent = note.dataset.appliedNote;
        })
        .catch(() => {
          /* Nothing to tell the visitor: the code is on the card in front of
             them and the Copy button works. */
        });
    };

    function open() {
      if (opened || dialog.open) return;
      opened = true;
      hideTab();
      paintArt();
      dialog.showModal();
      grid = null;
      paintFoil(dialog);

      if (document.fonts && document.fonts.status !== 'loaded') {
        document.fonts.ready.then(() => {
          if (!started && dialog.dataset.stage === 'scratch') paintFoil(dialog);
        });
      }
    }

    if (editing()) {
      /* In the editor it opens on its own, so the section can be seen while it
         is being edited, and again whenever it is selected in the sidebar.
         Nothing is written to storage in design mode, so closing and
         re-selecting always brings it back. */
      setTimeout(open, 800);
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.contains(dialog)) {
          opened = false;
          open();
        }
      });
      return;
    }

    /* A CART WITH SOMETHING IN IT IS NOT A VISITOR TO INTERRUPT.

       Measured 4 September 2026: after a successful add and with the drawer
       closed again, scrolling on to 35% opened this card over the whole
       390x844 viewport — elementFromPoint returned the dialog for every y
       from 4 to 116. showModal() puts it in the top layer, so the rest of
       the document is inert: a buyer who has chosen a garment and is on his
       way to the cart gets a newsletter sheet across the checkout path.

       The room guard above only sees the drawer while it is OPEN, which is
       exactly the wrong window — it protects the moment of adding and
       abandons every moment after it.

       /cart.js is read once, not watched: the card fires at most once per
       visit anyway, and a read that fails leaves the card free to appear",
       which is the behaviour this file already had. */
    const cartHasItems = () =>
      fetch('/cart.js', {headers: {Accept: 'application/json'}})
        .then((r) => (r.ok ? r.json() : null))
        .then((c) => !!(c && c.item_count > 0))
        .catch(() => false);

    const memory = remembered();
    if (memory.c) return;
    /* ANYTHING THE STRIP AT THE EDGE NEEDS MUST BE DECLARED ABOVE THIS LINE.
       This return is taken by every visitor who has closed the card, which is
       the only audience the strip has. A const below here is in its dead zone
       for them for ever. */
    if (memory.u && Date.now() < memory.u) return;

    /* ---------------------------------------------------------------------
       IT WAITS FOR THE ROOM TO BE EMPTY.

       Ella's cart drawer is a modal too, and two modals in the top layer at
       once means the second steals focus from whatever the visitor was doing in
       the first. A card that arrives over a half-finished cart is also the
       single worst moment for it to arrive. So: if something else is open, try
       again in a while, and give up quietly rather than queueing forever.
       ------------------------------------------------------------------- */
    /* THE CONSENT BANNER COUNTS AS SOMETHING BEING OPEN, and missing it was a
       real bug found on a phone on 21 August 2026. Shopify's cookie banner is
       neither a <dialog> nor a .modal--open — it is a fixed <div> that this
       guard could not see — so on a first visit the card arrived eight seconds
       in, on top of it. Measured at 390x844: the banner occupied 351x405 at the
       bottom, 48% of the screen, and showModal() puts the card in the TOP LAYER,
       which makes the rest of the document inert. Accept, Decline and Manage
       preferences all became untappable, so the one overlay a visitor is legally
       entitled to answer was the one they could not reach.

       #shopify-pc__banner is the banner, #shopify-pc__prefs the preferences
       modal behind its Manage button; either one means wait. They are checked by
       id because those ids are the stable part of that script's contract — the
       classes carry the same names, but the script builds the ids from an enum
       and the classes from a theme. */
    /* OUR OWN CART DRAWER COUNTS TOO, and missing it was the same bug a second
       time — the guard was written against ELLA's drawer and this shop does not
       use it. Both of the original two terms are dead here:
       sections/cooper-cart.liquid renders an ASIDE.cooper-cart, deliberately not
       a <dialog> (the reason is in that file's own comment at line 43 — a cart is
       a panel the visitor asked for, not a top-layer interruption), and nothing
       ever puts .modal--open on it; it signals open by setting data-open in
       assets/cooper-cart.js:155 (`root.dataset.open = ''`) and clearing it at
       line 170.

       THE BUG WAS: measured at 390 on / with the drawer open and visible and the
       consent banner already gone, this selector matched ZERO elements — the
       drawer neither matched nor contained a match — so attempt() concluded the
       room was empty, showModal() succeeded, and the card painted at 720x532 in
       the TOP LAYER (:modal true) over the open drawer. A top-layer modal makes
       the rest of the document inert, so the drawer's Checkout link could not be
       reached at all until the card was dismissed. That is this file's own
       "single worst moment for it to arrive", four paragraphs up.

       [data-cooper-cart][data-open] is the attribute pair, not the class:
       data-cooper-cart is the section's stable hook (sections/cooper-cart.liquid
       line 93) and .cooper-cart is only its paint. The drawer keeps the
       attribute for as long as it is on screen — it is removed before the
       close transition's `hidden` is restored (cooper-cart.js:170 then 178) —
       so the guard cannot false-positive on a closing panel's leftovers. The one
       gap left is two animation frames wide: open() sets hidden=false and only
       sets data-open inside a double requestAnimationFrame (cooper-cart.js:148
       and 153-156), so a card firing inside that ~33ms window still misses, and
       the retry does NOT save it — a miss opens straight away and never queues.
       Left as is on purpose: closing it means reading a second signal (root
       .hidden, which is false for the whole open panel) and that signal is also
       false during the drawer's close slide-out, which would make the card give
       up for 30s after every cart close. A ~33ms race is the cheaper of the
       two. */
    let tries = 0;
    const attempt = () => {
      if (opened) return;
      if (
        document.querySelector(
          'dialog[open], .modal--open, [data-cooper-cart][data-open], #shopify-pc__banner, #shopify-pc__prefs'
        )
      ) {
        tries += 1;
        if (tries > 10) return;
        setTimeout(attempt, 3000);
        return;
      }
      open();
    };

    /* ---------------------------------------------------------------------
       TWO TRIGGERS, FIRST ONE WINS.

       On the owner's call: "сделай тригеры на поп ап по времени а так же при
       скроле как делают большие еком сторы". They are not alternatives, they
       are two readings of the same signal — that somebody is actually looking
       at the shop. A visitor who reads gets there on the clock; one who scans
       gets there on the way down the page, often in two seconds. Arming only
       the clock misses the second kind entirely, which is most of them.

       `armed` is what makes them one trigger rather than two: whichever fires
       first disarms the other, so nothing can open the card twice.
       ------------------------------------------------------------------- */

    let armed = true;
    const fire = () => {
      if (!armed) return;
      armed = false;
      /* Проверка корзины стоит здесь, а не в attempt(): attempt повторяется
         раз в три секунды, пока комната занята, и десять запросов к /cart.js
         за тридцать секунд — не то, чем стоит платить за попап. */
      cartHasItems().then((has) => {
        if (has) return;
        attempt();
      });
    };

    /* ZERO ON THE CLOCK MEANS OFF, and until 3 September 2026 it meant the
       opposite: setTimeout(fire, 0) fires on the next tick, so the one value an
       owner would reach for to switch the clock off opened the card instantly.
       The scroll setting below has documented 0 as off since it was written; the
       two now agree. Both at zero arms nothing and the card never opens, which
       is the honest reading of "both triggers off" — the schema says so rather
       than leaving an owner to discover a dead section. */
    if (delay > 0) setTimeout(fire, delay);

    if (scrollAt > 0) {
      /* IT OPENS ON THE PAUSE, NOT MID-FLING. The owner's complaint of
         3 September 2026 was that the card "вылазит без повода" — arrives
         without a reason — and there are two readings of that. One is the
         setting, answered by turning the clock off. The other is the frame: the
         depth line gets crossed while the page is still moving under a thumb,
         and showModal() puts the card in the TOP LAYER in the same frame, so the
         next tap of a flick sequence lands on whatever the card has just put
         under the finger. The timer below is reset by every further scroll
         event, so it only runs out once the scrolling has actually stopped.
         350ms is the cost, and it buys a card that arrives at a moment the
         visitor has chosen to stand still. */
      let settle;
      const onScroll = () => {
        if (!armed) {
          window.removeEventListener('scroll', onScroll);
          return;
        }
        /* Depth through the scrollable range, not distance travelled. A short
           page has almost no range, and a percentage of the viewport height
           would fire on it after one flick — on a long page the same flick is
           two per cent. */
        const range = document.documentElement.scrollHeight - window.innerHeight;
        if (range < 200) return;
        if ((window.scrollY / range) * 100 < scrollAt) return;
        /* PAINTED AT THE THRESHOLD, not when the listener is armed. Armed is
           1.5s after load and happens to everyone; measured on the live shop the
           artwork was fetched in every session that stayed a second and a half,
           which is the 103 KB this change was supposed to save. Clarity puts the
           median paid session at 6% scroll depth — less than one screenful — so
           most visitors never reach this line at all and now never pay for it.

           The settle timer below gives the file 350ms of head start, and the
           foil canvas paints over it on open, so a late arrival is a picture
           appearing under a cover rather than an empty card. */
        paintArt();
        clearTimeout(settle);
        settle = setTimeout(() => {
          window.removeEventListener('scroll', onScroll);
          fire();
        }, 350);
      };

      /* A SECOND AND A HALF OF GRACE, and it is not a flourish. A browser
         restoring a scroll position on a back-navigation fires scroll events
         before the visitor has done anything, and a card that opens in the
         first frame of a returning visit reads as broken rather than as an
         offer. */
      setTimeout(() => {
        /* THE ONE-HARD-FLICK HOLE, which only became reachable once the clock
           stopped covering for it. Depth is read from scroll EVENTS, so a
           visitor who flings once past the line inside the grace window and
           then stops gets no further event, and with the clock off the card
           never comes at all. Reading the depth once at arming closes it —
           except where the browser put the visitor there rather than the
           visitor, which the Navigation Timing entry names outright instead of
           leaving it to be inferred from the scroll position.

           ONLY A FRESH NAVIGATION QUALIFIES, and testing on 3 September 2026
           is what narrowed it: history.scrollRestoration reads "auto" on this
           shop, so a RELOAD restores the old scroll position too, and the first
           version of this check excluded only back_forward. A visitor who
           refreshed a page halfway down would have been handed the card 1.5s
           later having done nothing at all — the owner's "без повода"
           reappearing through a different door. So the test is for what the
           visitor did, not for what it is not: type "navigate" and nothing
           else. A missing entry counts as not knowing, and not knowing waits
           for a real scroll event. */
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.type === 'navigate') {
          const range = document.documentElement.scrollHeight - window.innerHeight;
          if (range >= 200 && (window.scrollY / range) * 100 >= scrollAt) {
            fire();
            return;
          }
        }
        window.addEventListener('scroll', onScroll, {passive: true});
      }, 1500);
    }

    /* A resize changes the canvas box, and a backing store sized for the old
       one arrives stretched. Re-painting is only safe while the panel is
       untouched — after that the visitor's own work is in there. */
    let resizing;
    window.addEventListener('resize', () => {
      clearTimeout(resizing);
      resizing = setTimeout(() => {
        if (!started && dialog.open && dialog.dataset.stage === 'scratch') {
          grid = null;
          paintFoil(dialog);
        }
      }, 200);
    });
  }

  const scan = (root) => {
    const scope = root instanceof Element ? root : document;
    for (const dialog of scope.querySelectorAll('[data-scratch]')) wire(dialog);
  };

  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
